// Pure watch-time accumulator (no React, no DOM) so the counting rules can be unit tested.
//
// Time is measured on the media clock: a tick adds the amount currentTime advanced since the
// previous tick, but only when the video was genuinely playing for that whole interval and the
// advance is physically plausible for the real time that passed. That one rule covers:
//   - pause / buffering: currentTime does not advance, nothing is added
//   - seeking: currentTime jumps, the advance is far above what real time allows, it is dropped
//   - URL renewal reload (src swap, restart from 0): backwards / huge jump, dropped
//   - playback speed: at 2x the media clock legitimately advances 2s per real second
//   - throttled background timers: the real-time delta is measured, so a late tick still fits

// a tick may report up to 1.5x the speed-implied advance (timer jitter) plus half a second
const RATE_HEADROOM = 1.5;
const FIXED_HEADROOM_SECONDS = 0.5;

export class WatchTimeCounter {
	constructor(initial = {}) {
		this.watchedMs = initial.watchedMs || 0;
		this.wallMs = initial.wallMs || 0;
		this.lastPositionMs = initial.lastPositionMs || 0;
		this.maxRate = initial.maxRate || 1;
		this.prevActive = false;
		this.lastMedia = null;
		this.lastWall = null;
		this.seekPending = false;
	}

	// mediaTime: video.currentTime (s); wallNow: performance.now() (ms);
	// rate: video.playbackRate; active: playing && !seeking && enough data buffered
	// Returns the media seconds added by this tick.
	tick({ mediaTime, wallNow, rate, active, seeking = false }) {
		let added = 0;
		const safeRate = rate > 0 && Number.isFinite(rate) ? rate : 1;
		if (
			this.prevActive &&
			!this.seekPending &&
			this.lastWall !== null &&
			Number.isFinite(mediaTime)
		) {
			const wallDelta = (wallNow - this.lastWall) / 1000;
			const mediaDelta = mediaTime - this.lastMedia;
			if (
				wallDelta > 0 &&
				mediaDelta > 0 &&
				mediaDelta <=
					safeRate * wallDelta * RATE_HEADROOM + FIXED_HEADROOM_SECONDS
			) {
				this.watchedMs += mediaDelta * 1000;
				this.wallMs += wallDelta * 1000;
				added = mediaDelta;
			}
		}
		this.lastMedia = Number.isFinite(mediaTime) ? mediaTime : this.lastMedia;
		this.lastWall = wallNow;
		this.prevActive = Boolean(active);
		this.seekPending = Boolean(seeking);
		if (Number.isFinite(mediaTime)) {
			this.lastPositionMs = Math.round(mediaTime * 1000);
		}
		if (safeRate > this.maxRate) this.maxRate = safeRate;
		return added;
	}

	// the next interval may contain a jump: do not count it
	markSeek() {
		this.seekPending = true;
	}

	// source reloaded / errored / emptied: the baseline is no longer trustworthy
	markBreak() {
		this.prevActive = false;
	}

	get watchedSeconds() {
		return this.watchedMs / 1000;
	}

	get wallSeconds() {
		return this.wallMs / 1000;
	}
}
